import type { ClassificationClass, Lifecycle, ProductHealth } from '@aurora-pvi/contracts';
import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type { BoundaryEntry, Okr, Product, ProductCustomer, ValueStream } from './types';
import { at, clamp, groupBy, millions, pad, splitCents } from './support';

/**
 * The product portfolio: 24 curated products plus a generated tail.
 *
 * Health score and health band are never authored independently — the band is
 * always derived from the score. Two fields that can disagree will eventually
 * disagree, and "the tile says At Risk but the score says 84" is exactly the
 * kind of defect that ends a demo.
 */

/** Thresholds calibrated against the reference risk table (28 → Critical, 38 → At Risk). */
export function healthFromScore(score: number): ProductHealth {
  if (score >= 70) return 'Healthy';
  if (score >= 36) return 'AtRisk';
  if (score >= 30) return 'HighRisk';
  return 'Critical';
}

const TIER_LABELS: Record<Product['strategicTier'], string> = {
  Tier1: 'Mission Critical',
  Tier2: 'Business Critical',
  Tier3: 'Supporting',
};

interface CuratedProductSeed {
  code: string;
  name: string;
  parentKind: 'ProductGroup' | 'ValueStream';
  parentCode: string;
  valueStreamCode: string;
  departmentCode: string;
  classification: ClassificationClass;
  tier: Product['strategicTier'];
  healthScore: number;
  ownerPersonCode: string;
  technologyOwnerPersonCode: string;
  mission: string;
  goLiveDate: string;
  totalReleases: number;
  featuresDelivered: number;
  activeUsers: number | null;
  dailyTransactions: number | null;
  countries: number | null;
  riskDrivers: string[];
  aiUseCases: number;
  aiAutomationRate: number;
  aiCostSavingsMillions: number;
  aiHoursSaved: number;
  /** Relative weight when the value stream's business value is split. */
  valueWeight: number;
}

const CURATED: readonly CuratedProductSeed[] = [
  {
    code: 'PRD-CDR',
    name: 'Crew Disruption Recovery',
    parentKind: 'ProductGroup',
    parentCode: 'PG-CREWOPS',
    valueStreamCode: 'VS-CREW',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 87,
    ownerPersonCode: 'PPL-0046',
    technologyOwnerPersonCode: 'PPL-0060',
    mission:
      'Keep crews legal, rested and where the operation needs them when the day goes wrong — recovering a disrupted roster in minutes rather than hours, without breaching duty limits or crew agreements.',
    goLiveDate: '2019-03-15',
    totalReleases: 126,
    featuresDelivered: 412,
    activeUsers: 24318,
    dailyTransactions: 1_200_000,
    countries: 6,
    riskDrivers: [],
    aiUseCases: 14,
    aiAutomationRate: 0.28,
    aiCostSavingsMillions: 6.4,
    aiHoursSaved: 18200,
    valueWeight: 46,
  },
  {
    code: 'PRD-CRWP',
    name: 'Crew Planning & Rostering',
    parentKind: 'ProductGroup',
    parentCode: 'PG-CREWPLAN',
    valueStreamCode: 'VS-CREW',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 28,
    ownerPersonCode: 'PPL-0034',
    technologyOwnerPersonCode: 'PPL-0060',
    mission:
      'Build legal, efficient and fair crew pairings and rosters that the operation can actually fly.',
    goLiveDate: '2011-06-01',
    totalReleases: 88,
    featuresDelivered: 264,
    activeUsers: 9840,
    dailyTransactions: 340_000,
    countries: 6,
    riskDrivers: ['reliability', 'delivery', 'techdebt'],
    aiUseCases: 3,
    aiAutomationRate: 0.09,
    aiCostSavingsMillions: 1.1,
    aiHoursSaved: 3400,
    valueWeight: 30,
  },
  {
    code: 'PRD-CRTR',
    name: 'Crew Training Records',
    parentKind: 'ProductGroup',
    parentCode: 'PG-CREWPLAN',
    valueStreamCode: 'VS-CREW',
    departmentCode: 'DEP-NOPS',
    classification: 'SharedService',
    tier: 'Tier3',
    healthScore: 33,
    ownerPersonCode: 'PPL-0034',
    technologyOwnerPersonCode: 'PPL-0060',
    mission:
      'Hold a single, auditable record of every crew member’s qualifications, recency and licence currency.',
    goLiveDate: '2008-09-22',
    totalReleases: 41,
    featuresDelivered: 96,
    activeUsers: 11200,
    dailyTransactions: 42_000,
    countries: 6,
    riskDrivers: ['compliance', 'techdebt'],
    aiUseCases: 1,
    aiAutomationRate: 0.04,
    aiCostSavingsMillions: 0.3,
    aiHoursSaved: 900,
    valueWeight: 8,
  },
  {
    code: 'PRD-BOOK',
    name: 'Flight Booking',
    parentKind: 'ProductGroup',
    parentCode: 'PG-SHOP',
    valueStreamCode: 'VS-BOOK',
    departmentCode: 'DEP-CX',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 84,
    ownerPersonCode: 'PPL-0048',
    technologyOwnerPersonCode: 'PPL-0059',
    mission:
      'Turn a search into a booked, paid and ticketed trip in the fewest possible steps, on any channel.',
    goLiveDate: '2016-04-11',
    totalReleases: 214,
    featuresDelivered: 688,
    activeUsers: 41_200_000,
    dailyTransactions: 8_400_000,
    countries: 62,
    riskDrivers: [],
    aiUseCases: 9,
    aiAutomationRate: 0.22,
    aiCostSavingsMillions: 4.8,
    aiHoursSaved: 11400,
    valueWeight: 34,
  },
  {
    code: 'PRD-TRIP',
    name: 'Trip Management',
    parentKind: 'ProductGroup',
    parentCode: 'PG-TRIP',
    valueStreamCode: 'VS-BOOK',
    departmentCode: 'DEP-CX',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 78,
    ownerPersonCode: 'PPL-0042',
    technologyOwnerPersonCode: 'PPL-0059',
    mission:
      'Let a customer change, refund, upgrade or rebook a trip themselves, including when we disrupt it.',
    goLiveDate: '2017-10-02',
    totalReleases: 168,
    featuresDelivered: 501,
    activeUsers: 18_600_000,
    dailyTransactions: 2_100_000,
    countries: 62,
    riskDrivers: ['cost'],
    aiUseCases: 6,
    aiAutomationRate: 0.19,
    aiCostSavingsMillions: 3.2,
    aiHoursSaved: 8600,
    valueWeight: 24,
  },
  {
    code: 'PRD-CHKB',
    name: 'Check-in & Boarding',
    parentKind: 'ProductGroup',
    parentCode: 'PG-PAX',
    valueStreamCode: 'VS-ARPT',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 76,
    ownerPersonCode: 'PPL-0050',
    technologyOwnerPersonCode: 'PPL-0057',
    mission:
      'Get every eligible customer accepted, documented and boarded on time, across web, kiosk and gate.',
    goLiveDate: '2015-02-18',
    totalReleases: 152,
    featuresDelivered: 447,
    activeUsers: 38_400_000,
    dailyTransactions: 3_600_000,
    countries: 62,
    riskDrivers: ['reliability'],
    aiUseCases: 5,
    aiAutomationRate: 0.17,
    aiCostSavingsMillions: 2.6,
    aiHoursSaved: 7100,
    valueWeight: 30,
  },
  {
    code: 'PRD-BAGJ',
    name: 'Baggage Journey',
    parentKind: 'ProductGroup',
    parentCode: 'PG-BAG',
    valueStreamCode: 'VS-ARPT',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 32,
    ownerPersonCode: 'PPL-0049',
    technologyOwnerPersonCode: 'PPL-0057',
    mission:
      'Track every bag from acceptance to reclaim and recover the ones that go astray before the customer notices.',
    goLiveDate: '2013-07-30',
    totalReleases: 97,
    featuresDelivered: 288,
    activeUsers: 21400,
    dailyTransactions: 1_900_000,
    countries: 62,
    riskDrivers: ['reliability', 'cost', 'delivery'],
    aiUseCases: 4,
    aiAutomationRate: 0.12,
    aiCostSavingsMillions: 1.9,
    aiHoursSaved: 5200,
    valueWeight: 26,
  },
  {
    code: 'PRD-TURN',
    name: 'Turn Management',
    parentKind: 'ProductGroup',
    parentCode: 'PG-TURN',
    valueStreamCode: 'VS-ARPT',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 38,
    ownerPersonCode: 'PPL-0036',
    technologyOwnerPersonCode: 'PPL-0057',
    mission:
      'Sequence and monitor every task between arrival and departure so the aircraft leaves on schedule.',
    goLiveDate: '2018-11-05',
    totalReleases: 74,
    featuresDelivered: 203,
    activeUsers: 15600,
    dailyTransactions: 640_000,
    countries: 41,
    riskDrivers: ['delivery', 'reliability'],
    aiUseCases: 7,
    aiAutomationRate: 0.21,
    aiCostSavingsMillions: 2.2,
    aiHoursSaved: 6100,
    valueWeight: 22,
  },
  {
    code: 'PRD-ARMG',
    name: 'Airport Resource Management',
    parentKind: 'ProductGroup',
    parentCode: 'PG-TURN',
    valueStreamCode: 'VS-ARPT',
    departmentCode: 'DEP-NOPS',
    classification: 'PlatformProduct',
    tier: 'Tier2',
    healthScore: 56,
    ownerPersonCode: 'PPL-0036',
    technologyOwnerPersonCode: 'PPL-0057',
    mission:
      'Allocate gates, stands, belts and staff against the live schedule and reallocate them when it moves.',
    goLiveDate: '2014-05-19',
    totalReleases: 63,
    featuresDelivered: 174,
    activeUsers: 8900,
    dailyTransactions: 210_000,
    countries: 41,
    riskDrivers: ['techdebt'],
    aiUseCases: 2,
    aiAutomationRate: 0.08,
    aiCostSavingsMillions: 0.9,
    aiHoursSaved: 2400,
    valueWeight: 12,
  },
  {
    code: 'PRD-DISP',
    name: 'Flight Dispatch',
    parentKind: 'ProductGroup',
    parentCode: 'PG-DISPATCH',
    valueStreamCode: 'VS-FLTOPS',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 83,
    ownerPersonCode: 'PPL-0039',
    technologyOwnerPersonCode: 'PPL-0053',
    mission:
      'Produce a legal, fuel-efficient and signed operational flight plan for every departure.',
    goLiveDate: '2012-01-23',
    totalReleases: 118,
    featuresDelivered: 342,
    activeUsers: 3100,
    dailyTransactions: 5400,
    countries: 62,
    riskDrivers: [],
    aiUseCases: 6,
    aiAutomationRate: 0.24,
    aiCostSavingsMillions: 3.7,
    aiHoursSaved: 9400,
    valueWeight: 30,
  },
  {
    code: 'PRD-WBAL',
    name: 'Weight & Balance',
    parentKind: 'ProductGroup',
    parentCode: 'PG-DISPATCH',
    valueStreamCode: 'VS-FLTOPS',
    departmentCode: 'DEP-NOPS',
    classification: 'PlatformProduct',
    tier: 'Tier1',
    healthScore: 77,
    ownerPersonCode: 'PPL-0039',
    technologyOwnerPersonCode: 'PPL-0053',
    mission:
      'Compute and certify load, trim and centre of gravity for every departure without delaying the turn.',
    goLiveDate: '2010-08-14',
    totalReleases: 82,
    featuresDelivered: 191,
    activeUsers: 6200,
    dailyTransactions: 5400,
    countries: 62,
    riskDrivers: ['techdebt'],
    aiUseCases: 1,
    aiAutomationRate: 0.06,
    aiCostSavingsMillions: 0.6,
    aiHoursSaved: 1800,
    valueWeight: 18,
  },
  {
    code: 'PRD-FUEL',
    name: 'Fuel Optimisation',
    parentKind: 'ProductGroup',
    parentCode: 'PG-FUEL',
    valueStreamCode: 'VS-FLTOPS',
    departmentCode: 'DEP-NOPS',
    classification: 'DataProduct',
    tier: 'Tier2',
    healthScore: 74,
    ownerPersonCode: 'PPL-0040',
    technologyOwnerPersonCode: 'PPL-0053',
    mission:
      'Reduce fuel burn and carbon per block hour through better planning, tankering and in-flight advice.',
    goLiveDate: '2021-09-06',
    totalReleases: 46,
    featuresDelivered: 128,
    activeUsers: 2400,
    dailyTransactions: 18_000,
    countries: 62,
    riskDrivers: [],
    aiUseCases: 11,
    aiAutomationRate: 0.34,
    aiCostSavingsMillions: 8.1,
    aiHoursSaved: 4200,
    valueWeight: 22,
  },
  {
    code: 'PRD-NETP',
    name: 'Network Planning',
    parentKind: 'ValueStream',
    parentCode: 'VS-NETPLAN',
    valueStreamCode: 'VS-NETPLAN',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier2',
    healthScore: 72,
    ownerPersonCode: 'PPL-0024',
    technologyOwnerPersonCode: 'PPL-0053',
    mission:
      'Design a schedule that maximises contribution within fleet, slot and crew constraints.',
    goLiveDate: '2013-03-04',
    totalReleases: 57,
    featuresDelivered: 149,
    activeUsers: 640,
    dailyTransactions: 3200,
    countries: 62,
    riskDrivers: ['cost'],
    aiUseCases: 5,
    aiAutomationRate: 0.18,
    aiCostSavingsMillions: 4.4,
    aiHoursSaved: 3100,
    valueWeight: 34,
  },
  {
    code: 'PRD-MNTP',
    name: 'Maintenance Planning',
    parentKind: 'ValueStream',
    parentCode: 'VS-MTCE',
    valueStreamCode: 'VS-MTCE',
    departmentCode: 'DEP-NOPS',
    classification: 'BusinessProduct',
    tier: 'Tier2',
    healthScore: 61,
    ownerPersonCode: 'PPL-0023',
    technologyOwnerPersonCode: 'PPL-0053',
    mission:
      'Plan checks and defect rectification so aircraft are serviceable when the schedule needs them.',
    goLiveDate: '2009-11-17',
    totalReleases: 69,
    featuresDelivered: 182,
    activeUsers: 5800,
    dailyTransactions: 96_000,
    countries: 12,
    riskDrivers: ['techdebt', 'compliance'],
    aiUseCases: 4,
    aiAutomationRate: 0.13,
    aiCostSavingsMillions: 2.8,
    aiHoursSaved: 6400,
    valueWeight: 34,
  },
  {
    code: 'PRD-LOYL',
    name: 'Skyward Loyalty',
    parentKind: 'ProductGroup',
    parentCode: 'PG-LOYCORE',
    valueStreamCode: 'VS-LOYALTY',
    departmentCode: 'DEP-CX',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 81,
    ownerPersonCode: 'PPL-0043',
    technologyOwnerPersonCode: 'PPL-0059',
    mission:
      'Earn, hold and spend loyalty currency reliably enough that members plan their travel around it.',
    goLiveDate: '2015-06-29',
    totalReleases: 143,
    featuresDelivered: 398,
    activeUsers: 34_800_000,
    dailyTransactions: 4_100_000,
    countries: 62,
    riskDrivers: [],
    aiUseCases: 8,
    aiAutomationRate: 0.26,
    aiCostSavingsMillions: 5.1,
    aiHoursSaved: 7800,
    valueWeight: 40,
  },
  {
    code: 'PRD-LPST',
    name: 'Loyalty Partner Settlement',
    parentKind: 'ProductGroup',
    parentCode: 'PG-LOYCORE',
    valueStreamCode: 'VS-LOYALTY',
    departmentCode: 'DEP-CX',
    classification: 'SharedService',
    tier: 'Tier3',
    healthScore: 26,
    ownerPersonCode: 'PPL-0043',
    technologyOwnerPersonCode: 'PPL-0059',
    mission:
      'Reconcile and settle points bought and sold with co-brand, airline and retail partners.',
    goLiveDate: '2007-02-12',
    totalReleases: 34,
    featuresDelivered: 71,
    activeUsers: 210,
    dailyTransactions: 88_000,
    countries: 28,
    riskDrivers: ['techdebt', 'compliance', 'cost'],
    aiUseCases: 0,
    aiAutomationRate: 0.02,
    aiCostSavingsMillions: 0.1,
    aiHoursSaved: 400,
    valueWeight: 10,
  },
  {
    code: 'PRD-NOTF',
    name: 'Customer Notifications',
    parentKind: 'ValueStream',
    parentCode: 'VS-CARE',
    valueStreamCode: 'VS-CARE',
    departmentCode: 'DEP-CX',
    classification: 'PlatformProduct',
    tier: 'Tier2',
    healthScore: 58,
    ownerPersonCode: 'PPL-0026',
    technologyOwnerPersonCode: 'PPL-0054',
    mission:
      'Deliver the right message on the right channel at the right moment, once — not four times.',
    goLiveDate: '2018-01-08',
    totalReleases: 91,
    featuresDelivered: 246,
    activeUsers: 44_000_000,
    dailyTransactions: 12_600_000,
    countries: 62,
    riskDrivers: ['cost', 'reliability'],
    aiUseCases: 6,
    aiAutomationRate: 0.31,
    aiCostSavingsMillions: 2.4,
    aiHoursSaved: 5600,
    valueWeight: 22,
  },
  {
    code: 'PRD-DSCM',
    name: 'Disruption Communications',
    parentKind: 'ValueStream',
    parentCode: 'VS-CARE',
    valueStreamCode: 'VS-CARE',
    departmentCode: 'DEP-CX',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 49,
    ownerPersonCode: 'PPL-0026',
    technologyOwnerPersonCode: 'PPL-0054',
    mission:
      'Tell disrupted customers what has happened and what we are doing about it before they have to ask.',
    goLiveDate: '2020-02-26',
    totalReleases: 58,
    featuresDelivered: 157,
    activeUsers: 12_400_000,
    dailyTransactions: 890_000,
    countries: 62,
    riskDrivers: ['delivery'],
    aiUseCases: 9,
    aiAutomationRate: 0.37,
    aiCostSavingsMillions: 3.3,
    aiHoursSaved: 8900,
    valueWeight: 20,
  },
  {
    code: 'PRD-PRMG',
    name: 'Pricing & Revenue Management',
    parentKind: 'ValueStream',
    parentCode: 'VS-PRM',
    valueStreamCode: 'VS-PRM',
    departmentCode: 'DEP-COMM',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 80,
    ownerPersonCode: 'PPL-0028',
    technologyOwnerPersonCode: 'PPL-0054',
    mission:
      'Set and control fares and inventory so every seat is sold at the best achievable price.',
    goLiveDate: '2014-09-15',
    totalReleases: 131,
    featuresDelivered: 364,
    activeUsers: 1200,
    dailyTransactions: 26_000_000,
    countries: 62,
    riskDrivers: [],
    aiUseCases: 12,
    aiAutomationRate: 0.41,
    aiCostSavingsMillions: 11.6,
    aiHoursSaved: 9200,
    valueWeight: 46,
  },
  {
    code: 'PRD-ANCL',
    name: 'Ancillary Retailing',
    parentKind: 'ValueStream',
    parentCode: 'VS-RETAIL',
    valueStreamCode: 'VS-RETAIL',
    departmentCode: 'DEP-COMM',
    classification: 'BusinessProduct',
    tier: 'Tier1',
    healthScore: 40,
    ownerPersonCode: 'PPL-0029',
    technologyOwnerPersonCode: 'PPL-0054',
    mission:
      'Merchandise seats, bags, upgrades and partner products as a single offer wherever the customer is.',
    goLiveDate: '2019-07-22',
    totalReleases: 87,
    featuresDelivered: 231,
    activeUsers: 22_100_000,
    dailyTransactions: 3_300_000,
    countries: 62,
    riskDrivers: ['delivery', 'techdebt'],
    aiUseCases: 7,
    aiAutomationRate: 0.23,
    aiCostSavingsMillions: 3.9,
    aiHoursSaved: 4700,
    valueWeight: 40,
  },
  {
    code: 'PRD-CRGO',
    name: 'Cargo Booking',
    parentKind: 'ValueStream',
    parentCode: 'VS-CARGO',
    valueStreamCode: 'VS-CARGO',
    departmentCode: 'DEP-COMM',
    classification: 'BusinessProduct',
    tier: 'Tier2',
    healthScore: 54,
    ownerPersonCode: 'PPL-0030',
    technologyOwnerPersonCode: 'PPL-0054',
    mission:
      'Sell and manage belly and freighter capacity with accurate rates, capacity and handling instructions.',
    goLiveDate: '2016-11-30',
    totalReleases: 62,
    featuresDelivered: 168,
    activeUsers: 14200,
    dailyTransactions: 128_000,
    countries: 48,
    riskDrivers: ['cost'],
    aiUseCases: 3,
    aiAutomationRate: 0.14,
    aiCostSavingsMillions: 1.6,
    aiHoursSaved: 2900,
    valueWeight: 36,
  },
  {
    code: 'PRD-REVA',
    name: 'Revenue Accounting',
    parentKind: 'ProductGroup',
    parentCode: 'PG-REVACC',
    valueStreamCode: 'VS-FIN',
    departmentCode: 'DEP-CORP',
    classification: 'SharedService',
    tier: 'Tier2',
    healthScore: 35,
    ownerPersonCode: 'PPL-0051',
    technologyOwnerPersonCode: 'PPL-0054',
    mission:
      'Recognise passenger and cargo revenue accurately and settle interline in the accounting period it belongs to.',
    goLiveDate: '2006-04-03',
    totalReleases: 52,
    featuresDelivered: 118,
    activeUsers: 920,
    dailyTransactions: 9_400_000,
    countries: 62,
    riskDrivers: ['techdebt', 'compliance', 'reliability'],
    aiUseCases: 2,
    aiAutomationRate: 0.07,
    aiCostSavingsMillions: 1.4,
    aiHoursSaved: 5100,
    valueWeight: 30,
  },
  {
    code: 'PRD-PAYB',
    name: 'Payment & Billing',
    parentKind: 'ProductGroup',
    parentCode: 'PG-PAY',
    valueStreamCode: 'VS-FIN',
    departmentCode: 'DEP-CORP',
    classification: 'PlatformProduct',
    tier: 'Tier1',
    healthScore: 79,
    ownerPersonCode: 'PPL-0045',
    technologyOwnerPersonCode: 'PPL-0054',
    mission:
      'Take money in every market we sell in, safely, and give it back promptly when we have to.',
    goLiveDate: '2017-05-16',
    totalReleases: 124,
    featuresDelivered: 318,
    activeUsers: 41_600_000,
    dailyTransactions: 6_800_000,
    countries: 62,
    riskDrivers: ['compliance'],
    aiUseCases: 5,
    aiAutomationRate: 0.2,
    aiCostSavingsMillions: 2.9,
    aiHoursSaved: 4400,
    valueWeight: 28,
  },
  {
    code: 'PRD-C360',
    name: 'Customer 360',
    parentKind: 'ValueStream',
    parentCode: 'VS-DATAAI',
    valueStreamCode: 'VS-DATAAI',
    departmentCode: 'DEP-DATA',
    classification: 'DataProduct',
    tier: 'Tier2',
    healthScore: 63,
    ownerPersonCode: 'PPL-0052',
    technologyOwnerPersonCode: 'PPL-0033',
    mission:
      'Resolve one trustworthy customer identity and profile that every channel and product can rely on.',
    goLiveDate: '2022-03-28',
    totalReleases: 48,
    featuresDelivered: 134,
    activeUsers: 3400,
    dailyTransactions: 21_000_000,
    countries: 62,
    riskDrivers: ['dataquality'],
    aiUseCases: 10,
    aiAutomationRate: 0.29,
    aiCostSavingsMillions: 4.6,
    aiHoursSaved: 6800,
    valueWeight: 40,
  },
];

/**
 * Generated-product names, per value stream. Written out rather than assembled
 * from noun bags because "Crew Bid Line Management" is recognisable to an
 * airline audience and "Crew Service Platform 7" is not.
 */
const NAME_BANK: Record<string, readonly string[]> = {
  'VS-FLTOPS': [
    'Flight Following',
    'Runway Performance Analysis',
    'Electronic Flight Bag',
    'Flight Data Monitoring',
    'NOTAM & Briefing Distribution',
    'ETOPS Compliance Tracking',
    'Airspace Charges Reconciliation',
    'Route & Airway Catalogue',
    'Operational Flight Log Capture',
    'Ops Performance Reporting',
    'Diversion Support',
    'Datalink Message Routing',
  ],
  'VS-CREW': [
    'Crew Bid Line Management',
    'Crew Pay & Allowance Calculation',
    'Crew Hotel & Transport',
    'Crew Fatigue Risk Modelling',
    'Crew Communications',
    'Crew Qualification Forecasting',
    'Cabin Crew Scheduling',
    'Crew Portal',
  ],
  'VS-ARPT': [
    'Gate & Stand Allocation',
    'Ground Handling Instructions',
    'Passenger Boarding Verification',
    'Baggage Reconciliation Reporting',
    'Airport Staff Rostering',
    'Lounge Access Management',
    'Special Assistance Coordination',
    'Ramp Safety Reporting',
    'Airport Signage & Displays',
    'De-icing Coordination',
    'Aircraft Servicing Schedules',
    'Kiosk Fleet Management',
  ],
  'VS-MTCE': [
    'Technical Records',
    'Line Maintenance Control',
    'Component Reliability Analytics',
    'Aircraft Defect Management',
    'Maintenance Parts Inventory',
    'Engineering Change Management',
    'Tooling & Calibration',
    'Airworthiness Directive Tracking',
    'Hangar Slot Planning',
    'Maintenance Workforce Certification',
  ],
  'VS-NETPLAN': [
    'Slot Coordination',
    'Fleet Assignment',
    'Codeshare Schedule Management',
    'Schedule Change Distribution',
    'Route Profitability Analytics',
    'Airport Charges Modelling',
    'Seasonal Capacity Planning',
    'Partner Connectivity Planning',
  ],
  'VS-BOOK': [
    'Offer Management',
    'Order Management',
    'Fare Filing & Distribution',
    'Availability & Inventory Service',
    'Seat Selection',
    'Group Bookings',
    'Corporate Booking Tool Integration',
    'Travel Agency Portal',
    'Booking Fraud Screening',
    'Refund Processing',
    'Ticketing & Document Issue',
    'Interline & Codeshare Booking',
    'Trip Search Experience',
    'Mobile Trip Companion',
    'Booking Modification Engine',
    'Waitlist & Standby Management',
    'Voucher & Credit Management',
    'Booking Analytics',
  ],
  'VS-CARE': [
    'Contact Centre Routing',
    'Case Management',
    'Customer Feedback & NPS',
    'Compensation & Claims',
    'Virtual Assistant',
    'Knowledge Base',
    'Social Care',
    'Customer Consent Management',
    'Service Recovery Offers',
    'Customer Effort Analytics',
    'Care Workforce Scheduling',
  ],
  'VS-LOYALTY': [
    'Member Enrolment',
    'Tier & Status Management',
    'Points Accrual Engine',
    'Award Redemption',
    'Co-brand Card Integration',
    'Partner Earn Management',
    'Loyalty Promotions',
    'Member Communications',
    'Loyalty Fraud Detection',
    'Elite Benefits Delivery',
    'Points Liability Reporting',
    'Loyalty Shopping Portal',
    'Family Pooling',
  ],
  'VS-PRM': [
    'Fare Construction',
    'Inventory Control',
    'Demand Forecasting',
    'Overbooking Optimisation',
    'Group Pricing',
    'Competitive Fare Monitoring',
    'Revenue Integrity',
    'Continuous Pricing Engine',
    'Bid Price Calculation',
    'Market Performance Analytics',
    'Promotional Fare Management',
    'Distribution Cost Analytics',
  ],
  'VS-RETAIL': [
    'Seat Merchandising',
    'Bag Merchandising',
    'Upgrade Offers',
    'Bundle Construction',
    'Third-party Retail Partners',
    'Onboard Retail',
    'Dynamic Offer Personalisation',
    'Ancillary Catalogue',
    'Ancillary Revenue Reporting',
    'Subscription Products',
    'Retail Experimentation',
  ],
  'VS-CARGO': [
    'Cargo Capacity Management',
    'Air Waybill Processing',
    'Cargo Handling Instructions',
    'Dangerous Goods Compliance',
    'Cargo Rate Management',
    'Cargo Track & Trace',
    'Cargo Settlement',
    'Mail Handling',
  ],
  'VS-FIN': [
    'General Ledger Integration',
    'Interline Settlement',
    'Proration',
    'Tax & Fee Calculation',
    'Accounts Payable Automation',
    'Procure-to-Pay',
    'Treasury & FX',
    'Fixed Asset Management',
    'Financial Planning & Analysis',
    'Statutory Reporting',
    'Expense Management',
    'Chargeback & Dispute Handling',
    'Merchant Settlement',
    'Audit Evidence Repository',
  ],
  'VS-PEOPLE': [
    'Core HR Records',
    'Recruitment & Onboarding',
    'Learning Management',
    'Time & Attendance',
    'Payroll Interface',
    'Employee Portal',
    'Workforce Analytics',
    'Succession & Talent',
    'Occupational Health',
    'Workplace Facilities',
    'Corporate Travel',
    'Employee Case Management',
  ],
  'VS-DATAAI': [
    'Enterprise Data Hub',
    'Operational Data Streaming',
    'Self-service Analytics',
    'ML Feature Store',
    'Model Deployment Platform',
    'Data Quality Monitoring',
    'Data Catalogue & Lineage',
    'Master Data Management',
    'Commercial Analytics',
    'Operations Analytics',
    'AI Assistant Platform',
    'Experimentation Platform',
    'Reporting Distribution',
    'Data Sharing & Monetisation',
    'Privacy & Consent Analytics',
  ],
};

/** Generated products per value stream. Uneven on purpose. */
const GENERATED_COUNTS: Record<string, number> = {
  'VS-FLTOPS': 9,
  'VS-CREW': 6,
  'VS-ARPT': 11,
  'VS-MTCE': 8,
  'VS-NETPLAN': 6,
  'VS-BOOK': 18,
  'VS-CARE': 11,
  'VS-LOYALTY': 13,
  'VS-PRM': 12,
  'VS-RETAIL': 11,
  'VS-CARGO': 8,
  'VS-FIN': 14,
  'VS-PEOPLE': 12,
  'VS-DATAAI': 15,
};

/**
 * The enterprise health mix from the reference screen, expressed as counts of
 * *active* products. Fixed rather than sampled so the doughnut always reads
 * 45 / 30 / 13 / 12 and the QA gate can assert it.
 */
const ACTIVE_HEALTH_MIX: Record<Exclude<ProductHealth, 'Unknown'>, number> = {
  Healthy: 64,
  AtRisk: 42,
  HighRisk: 18,
  Critical: 18,
};

const HEALTH_DIMENSIONS = [
  'Reliability',
  'Performance',
  'Adoption',
  'Delivery',
  'Security',
  'Compliance',
];

const RISK_DRIVER_POOL = [
  'reliability',
  'cost',
  'delivery',
  'techdebt',
  'compliance',
  'dataquality',
  'supplier',
];

function scoreForHealth(health: ProductHealth, rng: Rng): number {
  switch (health) {
    case 'Healthy':
      return rng.int(70, 94);
    case 'AtRisk':
      return rng.int(36, 69);
    case 'HighRisk':
      return rng.int(30, 35);
    case 'Critical':
      return rng.int(14, 29);
    default:
      return 50;
  }
}

function buildOkrs(code: string, health: ProductHealth, rng: Rng): Okr[] {
  const templates: [string, string, string][] =
    code === 'PRD-CDR'
      ? [
          ['Improve crew schedule adherence', '≥ 97%', '95.6%'],
          ['Reduce crew disruption cost', '$18M', '$16.2M'],
          ['Enhance crew experience (NPS)', '≥ 55', '48'],
        ]
      : [
          ['Improve service reliability', '≥ 99.9%', `${rng.float(99.2, 99.98).toFixed(2)}%`],
          ['Reduce unit cost to serve', '-8%', `-${rng.float(1, 9).toFixed(1)}%`],
          ['Raise adoption of self-service', '≥ 80%', `${rng.int(52, 88)}%`],
        ];

  return templates.map((template, index) => {
    const progress = clamp(
      rng.around(health === 'Healthy' ? 0.82 : health === 'AtRisk' ? 0.62 : 0.41, 0.16, 0.08, 1),
      0.08,
      1,
    );
    return {
      id: idFor('Okr', `${code}-OKR-${index + 1}`),
      ordinal: index + 1,
      objective: at(template, 0),
      targetLabel: at(template, 1),
      actualLabel: at(template, 2),
      progress: Math.round(progress * 1000) / 1000,
      onTrack: progress >= 0.7,
    };
  });
}

function buildCustomers(code: string, name: string): ProductCustomer[] {
  if (code === 'PRD-CDR') {
    return [
      {
        id: idFor('Customer', `${code}-1`),
        name: 'Crew Scheduling',
        segmentType: 'Internal',
        isPrimary: true,
        iconToken: 'people',
      },
      {
        id: idFor('Customer', `${code}-2`),
        name: 'Crew Members',
        segmentType: 'Internal',
        isPrimary: true,
        iconToken: 'badge',
      },
      {
        id: idFor('Customer', `${code}-3`),
        name: 'In-Flight Operations',
        segmentType: 'Internal',
        isPrimary: false,
        iconToken: 'plane',
      },
      {
        id: idFor('Customer', `${code}-4`),
        name: 'Airport Operations',
        segmentType: 'Internal',
        isPrimary: false,
        iconToken: 'tower',
      },
      {
        id: idFor('Customer', `${code}-5`),
        name: 'Operations Control Centre',
        segmentType: 'Internal',
        isPrimary: false,
        iconToken: 'gauge',
      },
    ];
  }
  return [
    {
      id: idFor('Customer', `${code}-1`),
      name: `${name} users`,
      segmentType: 'Internal',
      isPrimary: true,
      iconToken: 'people',
    },
    {
      id: idFor('Customer', `${code}-2`),
      name: 'Travelling customers',
      segmentType: 'External',
      isPrimary: false,
      iconToken: 'user',
    },
  ];
}

function buildBoundary(code: string, name: string): BoundaryEntry[] {
  if (code === 'PRD-CDR') {
    const inScope = [
      'Crew Planning & Rostering hand-off',
      'Pairing re-optimisation (day of ops)',
      'Reserve Management',
      'Crew Tracking (day of ops)',
      'Crew Alerts & Notifications',
      'Fatigue & Duty Compliance checks',
    ];
    const outOfScope: [string, string][] = [
      ['Time & Attendance', 'Owned by People & Workplace; consumed as a feed.'],
      ['Payroll Processing', 'Financial system of record sits in Finance ERP.'],
      ['Learning Management', 'Crew Training Records owns qualification currency.'],
      ['Travel & Expenses', 'Corporate shared service.'],
      ['HR Benefits', 'Corporate shared service.'],
      [
        'Third-party Training Delivery',
        'Supplier-operated; contract-managed, not product-managed.',
      ],
    ];
    return [
      ...inScope.map((label, index) => ({
        id: idFor('Boundary', `${code}-IN-${index + 1}`),
        label,
        inScope: true,
        reason: null,
        evidenceCode: index === 0 ? 'EVD-CDR-01' : null,
      })),
      ...outOfScope.map(([label, reason], index) => ({
        id: idFor('Boundary', `${code}-OUT-${index + 1}`),
        label,
        inScope: false,
        reason,
        evidenceCode: null,
      })),
    ];
  }
  return [
    {
      id: idFor('Boundary', `${code}-IN-1`),
      label: `${name} core capability`,
      inScope: true,
      reason: null,
      evidenceCode: null,
    },
    {
      id: idFor('Boundary', `${code}-IN-2`),
      label: `${name} reporting and analytics`,
      inScope: true,
      reason: null,
      evidenceCode: null,
    },
    {
      id: idFor('Boundary', `${code}-OUT-1`),
      label: 'Identity and access management',
      inScope: false,
      reason: 'Enterprise shared service.',
      evidenceCode: null,
    },
    {
      id: idFor('Boundary', `${code}-OUT-2`),
      label: 'General ledger posting',
      inScope: false,
      reason: 'Owned by Finance & Revenue Accounting.',
      evidenceCode: null,
    },
  ];
}

function buildHealthHistory(current: number, rng: Rng): number[] {
  // Walk backwards from today so the last point always equals the headline
  // score — a sparkline that ends somewhere other than the number beside it is
  // the kind of detail an executive notices immediately.
  const points: number[] = [current];
  let value = current;
  for (let i = 0; i < 11; i += 1) {
    value = clamp(Math.round(value + rng.around(0, 4, -9, 9)), 8, 97);
    points.unshift(value);
  }
  return points;
}

export function buildProducts(
  asOf: string,
  rng: Rng,
  valueStreams: readonly ValueStream[],
): Product[] {
  const productRng = rng.fork('products');
  const stamp = provenanceFactory(asOf, rng.fork('products-provenance'));
  const products: Product[] = [];

  const makeProduct = (
    input: Omit<
      Product,
      'id' | 'healthHistory' | 'health' | 'businessValueCents' | 'productGroupId'
    > & {
      businessValueCents: number;
    },
  ): Product => ({
    ...input,
    id: idFor('Product', input.businessCode),
    productGroupId:
      input.parentKind === 'ProductGroup' ? idFor('ProductGroup', input.parentCode) : null,
    health: healthFromScore(input.healthScore),
    healthHistory: buildHealthHistory(input.healthScore, productRng),
  });

  for (const seed of CURATED) {
    products.push(
      makeProduct({
        businessCode: seed.code,
        name: seed.name,
        parentKind: seed.parentKind,
        parentCode: seed.parentCode,
        valueStreamCode: seed.valueStreamCode,
        departmentCode: seed.departmentCode,
        classification: seed.classification,
        lifecycle: 'Active',
        healthScore: seed.healthScore,
        ownerPersonCode: seed.ownerPersonCode,
        technologyOwnerPersonCode: seed.technologyOwnerPersonCode,
        strategicTier: seed.tier,
        strategicTierLabel: TIER_LABELS[seed.tier],
        mission: seed.mission,
        isCurated: true,
        businessValueCents: 0,
        goLiveDate: seed.goLiveDate,
        totalReleases: seed.totalReleases,
        featuresDelivered: seed.featuresDelivered,
        activeUsers: seed.activeUsers,
        dailyTransactions: seed.dailyTransactions,
        countries: seed.countries,
        riskDrivers: seed.riskDrivers,
        healthDimensions: HEALTH_DIMENSIONS.map((dimension, index) => {
          const offset = index === 3 && seed.riskDrivers.includes('delivery') ? -30 : 0;
          const score = clamp(seed.healthScore + offset + (index % 3) * 3, 5, 98);
          const health = healthFromScore(score);
          return {
            dimension,
            status: health,
            label: health === 'Healthy' ? 'Good' : health === 'AtRisk' ? 'At Risk' : 'Poor',
          };
        }),
        okrs: buildOkrs(seed.code, healthFromScore(seed.healthScore), productRng),
        customers: buildCustomers(seed.code, seed.name),
        boundary: buildBoundary(seed.code, seed.name),
        aiUseCasesInProduction: seed.aiUseCases,
        aiAutomationRate: seed.aiAutomationRate,
        aiCostSavingsCents: millions(seed.aiCostSavingsMillions),
        aiHoursSaved: seed.aiHoursSaved,
        provenance: stamp(SOURCE_SYSTEMS.productRegistry, `PR-${seed.code}`, {
          meanAgeDays: 4,
          meanConfidence: 0.94,
        }),
      }),
    );
  }

  // ── Generated tail ─────────────────────────────────────────────────────────
  // Exactly 36 of the 154 generated products are given a non-Active lifecycle,
  // spread evenly across the value streams. Fixing the count rather than
  // sampling it is what lets the enterprise land on precisely 142 active
  // products, which in turn lets the health mix below be exact.
  const generatedTotal = Object.values(GENERATED_COUNTS).reduce((sum, n) => sum + n, 0);
  const nonActiveTarget = 36;
  const nonActiveSlots = new Set<number>();
  for (let k = 0; k < nonActiveTarget; k += 1) {
    nonActiveSlots.add(Math.floor((k * generatedTotal) / nonActiveTarget));
  }
  const nonActiveCycle: Lifecycle[] = [
    'Candidate',
    'Sunset',
    'Candidate',
    'Proposed',
    'Sunset',
    'Candidate',
    'Retired',
    'Proposed',
    'Sunset',
    'Candidate',
    'Superseded',
    'Sunset',
  ];
  const classificationCycle: ClassificationClass[] = [
    'BusinessProduct',
    'PlatformProduct',
    'BusinessProduct',
    'SharedService',
    'DataProduct',
    'BusinessProduct',
    'ApplicationComponent',
    'BusinessProduct',
    'PlatformProduct',
    'UtilityCommodity',
    'BusinessProduct',
    'ProjectInitiative',
  ];

  let generatedIndex = 0;
  for (const stream of valueStreams) {
    const count = GENERATED_COUNTS[stream.businessCode] ?? 0;
    const bank = NAME_BANK[stream.businessCode] ?? [];
    for (let i = 0; i < count; i += 1) {
      const name = bank[i] ?? `${stream.name} Capability ${i + 1}`;
      const code = `PRD-G${pad(generatedIndex + 1, 3)}`;
      const lifecycle: Lifecycle = nonActiveSlots.has(generatedIndex)
        ? at(nonActiveCycle, generatedIndex % nonActiveCycle.length)
        : 'Active';
      const classification =
        lifecycle === 'Candidate'
          ? 'Undetermined'
          : at(classificationCycle, generatedIndex % classificationCycle.length);
      const tier: Product['strategicTier'] =
        generatedIndex % 7 === 0 ? 'Tier1' : generatedIndex % 3 === 0 ? 'Tier2' : 'Tier3';

      products.push(
        makeProduct({
          businessCode: code,
          name,
          parentKind: 'ValueStream',
          parentCode: stream.businessCode,
          valueStreamCode: stream.businessCode,
          departmentCode: stream.departmentCode,
          classification,
          lifecycle,
          // Provisional; rewritten below so the enterprise mix is exact.
          healthScore: 60,
          ownerPersonCode: stream.ownerPersonCode,
          technologyOwnerPersonCode: stream.ownerPersonCode,
          strategicTier: tier,
          strategicTierLabel: TIER_LABELS[tier],
          mission: `Deliver ${name.toLowerCase()} for ${stream.name} at the reliability and unit cost the operation expects.`,
          isCurated: false,
          businessValueCents: 0,
          goLiveDate:
            lifecycle === 'Candidate' || lifecycle === 'Proposed'
              ? null
              : `20${pad(productRng.int(6, 24), 2)}-${pad(productRng.int(1, 12), 2)}-${pad(productRng.int(1, 28), 2)}`,
          totalReleases: productRng.int(4, 96),
          featuresDelivered: productRng.int(12, 280),
          activeUsers: productRng.chance(0.85) ? productRng.int(120, 2_400_000) : null,
          dailyTransactions: productRng.chance(0.8) ? productRng.int(400, 900_000) : null,
          countries: productRng.chance(0.7) ? productRng.int(1, 62) : null,
          riskDrivers: [],
          healthDimensions: [],
          okrs: [],
          customers: buildCustomers(code, name),
          boundary: buildBoundary(code, name),
          aiUseCasesInProduction: productRng.chance(0.35) ? productRng.int(1, 6) : 0,
          aiAutomationRate: Math.round(productRng.float(0, 0.34) * 100) / 100,
          aiCostSavingsCents: millions(Math.round(productRng.float(0, 3.4) * 10) / 10),
          aiHoursSaved: productRng.int(0, 5200),
          provenance: stamp(
            SOURCE_SYSTEMS.productRegistry,
            `PR-${code}`,
            generatedIndex % 23 === 0
              ? { meanAgeDays: 70, meanConfidence: 0.48 } // a stale, poorly evidenced corner
              : { meanAgeDays: 9, meanConfidence: 0.81 },
          ),
        }),
      );
      generatedIndex += 1;
    }
  }

  // ── Force the enterprise health mix ────────────────────────────────────────
  const activeGenerated = products.filter((p) => !p.isCurated && p.lifecycle === 'Active');
  const curatedActive = products.filter((p) => p.isCurated && p.lifecycle === 'Active');
  const remaining: Record<string, number> = { ...ACTIVE_HEALTH_MIX };
  for (const product of curatedActive) {
    const key = product.health;
    if (key in remaining) remaining[key] = (remaining[key] ?? 0) - 1;
  }
  const quota: ProductHealth[] = [];
  for (const [health, count] of Object.entries(remaining)) {
    for (let i = 0; i < Math.max(0, count); i += 1) quota.push(health as ProductHealth);
  }
  const shuffledQuota = productRng.shuffle(quota);

  activeGenerated.forEach((product, index) => {
    const health = shuffledQuota[index] ?? 'AtRisk';
    product.healthScore = scoreForHealth(health, productRng);
    product.health = healthFromScore(product.healthScore);
    product.healthHistory = buildHealthHistory(product.healthScore, productRng);
    product.riskDrivers =
      product.health === 'Healthy'
        ? []
        : productRng.sample(RISK_DRIVER_POOL, product.health === 'Critical' ? 3 : 2);
    product.healthDimensions = HEALTH_DIMENSIONS.map((dimension, i) => {
      const score = clamp(product.healthScore + (i % 3) * 4 - 4, 5, 98);
      const status = healthFromScore(score);
      return {
        dimension,
        status,
        label: status === 'Healthy' ? 'Good' : status === 'AtRisk' ? 'At Risk' : 'Poor',
      };
    });
    product.okrs = buildOkrs(product.businessCode, product.health, productRng);
  });

  // Non-active products get a score too, but they are excluded from the mix.
  for (const product of products.filter((p) => p.lifecycle !== 'Active')) {
    const health: ProductHealth =
      product.lifecycle === 'Candidate' || product.lifecycle === 'Proposed' ? 'Unknown' : 'AtRisk';
    product.healthScore = health === 'Unknown' ? 0 : scoreForHealth('AtRisk', productRng);
    product.health = health === 'Unknown' ? 'Unknown' : healthFromScore(product.healthScore);
    product.healthHistory =
      health === 'Unknown' ? [] : buildHealthHistory(product.healthScore, productRng);
  }

  // ── Business value: split each value stream's total across its products ────
  // Splitting rather than summing means the hierarchy roll-up is exact by
  // construction: no rounding gap ever appears between a value stream tile and
  // the products beneath it.
  const byStream = groupBy(products, (p) => p.valueStreamCode);
  for (const stream of valueStreams) {
    const inStream = (byStream.get(stream.businessCode) ?? []).filter(
      (p) => p.lifecycle === 'Active' || p.lifecycle === 'Sunset',
    );
    if (inStream.length === 0) continue;
    const weights = inStream.map((product) => {
      const curated = CURATED.find((c) => c.code === product.businessCode);
      if (curated) return curated.valueWeight;
      return product.strategicTier === 'Tier1' ? 9 : product.strategicTier === 'Tier2' ? 5 : 2;
    });
    const shares = splitCents(stream.annualBusinessValueCents, weights);
    inStream.forEach((product, index) => {
      product.businessValueCents = at(shares, index);
    });
  }

  return products;
}

export const CURATED_PRODUCT_CODES = CURATED.map((seed) => seed.code);
export const NORTH_STAR_PRODUCT_CODE = 'PRD-CDR';
